import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { QuestionRendererProps } from './types';
export function BarChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || question.interactiveConfig || {};
  // Normalize data: support both {data: [{label, value}]} and {labels: [], values: []} formats
  let data: Array<{
    label: string;
    value: number;
  }> = [];
  if (Array.isArray(config.data) && config.data.length > 0) {
    // Canonical format: data is array of {label, value}
    data = config.data;
  } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
    // Alternative format: separate labels and values arrays
    data = config.labels.map((label: string, i: number) => ({
      label,
      value: config.values[i] ?? 0
    }));
  }
  const xAxisLabel = config.xAxisLabel || '';
  const yAxisLabel = config.yAxisLabel || '';
  const barColor = config.barColor || '#6366f1';
  const activeBarColor = config.activeBarColor || '#4f46e5';
  const hideValues = config.hideValues !== false; // Default: hide values
  const prevValidateRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Reset state on question change
  useEffect(() => {
    setSelectedIndex(null);
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedIndex !== null) {
      prevValidateRef.current = true;
      onAnswer(String(selectedIndex));
    }
  }, [shouldValidate, selectedIndex, onAnswer]);
  const handleBarClick = (_data: any, index: number) => {
    setSelectedIndex(index === selectedIndex ? null : index);
  };
  // Custom tooltip that only shows the label, not the value
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-4639">
        <span className="font-semibold text-slate-700" data-id="element-4640">{item.label}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-4641">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-4642">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-id="element-4643">
        <ResponsiveContainer width="100%" height="100%" data-id="element-4644">
          <BarChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-4645">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-4646" />
            <XAxis dataKey="label" tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-4647" />

            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-4648" />

            <Tooltip content={<CustomTooltip data-id="element-4650" />} cursor={{
            fill: '#f1f5f9'
          }} data-id="element-4649" />

            <Bar dataKey="value" onClick={handleBarClick} cursor="pointer" radius={[4, 4, 0, 0]} data-id="element-4651">
              {data.map((_entry: any, index: number) => <Cell key={`cell-${index}`} fill={selectedIndex === index ? activeBarColor : barColor} stroke={selectedIndex === index ? '#312e81' : 'none'} strokeWidth={2} data-id="element-4652" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-4653">
        Klik op de juiste staaf in de grafiek
      </p>
    </div>;
}